import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryDropdown, setIsCategoryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryDropdown(false);
    setSearchQuery('');
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/');
      setSearchQuery('');
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500">
                <i className="ri-book-open-line text-background-50 text-lg"></i>
              </div>
              <span className={`font-heading text-xl font-bold ${isScrolled ? 'text-foreground-950' : 'text-background-50'}`}>
                妖精漫画
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/'
                    ? 'text-accent-500'
                    : isScrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                首页
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsCategoryDropdown(!isCategoryDropdown)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    location.pathname.startsWith('/category')
                      ? 'text-accent-500'
                      : isScrolled
                      ? 'text-foreground-700 hover:text-primary-500'
                      : 'text-background-50/90 hover:text-background-50'
                  }`}
                >
                  分类浏览
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${isCategoryDropdown ? 'rotate-180' : ''}`}></i>
                </button>
                {isCategoryDropdown && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-lg py-2 min-w-[200px] border border-background-200/70">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                        <span className="ml-2 text-xs text-foreground-400">({cat.comicCount})</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/category/romance"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/category/romance'
                    ? 'text-accent-500'
                    : isScrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                热门排行
              </Link>
              <Link
                to="/about"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/about'
                    ? 'text-accent-500'
                    : isScrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画..."
                className={`w-48 pl-9 pr-4 py-2 text-sm rounded-full border transition-all focus:outline-none focus:w-64 ${
                  isScrolled
                    ? 'bg-background-100 border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:border-primary-400'
                    : 'bg-background-50/20 border-background-50/30 text-background-50 placeholder-background-50/60 focus:border-background-50'
                }`}
              />
              <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer">
                <i className={`ri-search-line text-sm ${isScrolled ? 'text-foreground-400' : 'text-background-50/70'}`}></i>
              </button>
            </form>
            <Link
              to="/about"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50/20 text-background-50 border border-background-50/30 hover:bg-background-50/30'
              }`}
            >
              VIP专区
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-foreground-800' : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画..."
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-full border border-background-200/70 bg-background-100 text-foreground-800 focus:outline-none focus:border-primary-400"
              />
              <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer">
                <i className="ri-search-line text-sm text-foreground-400"></i>
              </button>
            </div>
          </form>
          <div className="flex flex-col gap-1">
            <Link to="/" className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors">首页</Link>
            <div className="px-3 py-2.5 text-sm text-foreground-500 font-medium">分类浏览</div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="pl-6 pr-3 py-2 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors">关于我们</Link>
            <Link to="/about" className="mt-2 px-5 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-background-50 text-center hover:bg-primary-600 transition-colors whitespace-nowrap">VIP专区</Link>
          </div>
        </div>
      )}
    </nav>
  );
}